import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./styles/index.css";
import {
  DefaultOptions,
  QueryClient,
  QueryClientProvider,
  focusManager,
} from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster.tsx";
import * as Sentry from "@sentry/react";
import { withSentryMutation } from "./lib/sentryUtils.ts";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Capacitor } from "@capacitor/core";

Sentry.init({
  dsn: "https://4d1a4b50cfb236a97ac98ff6d86ca9c4@o4509266424954880.ingest.us.sentry.io/4509277648977920",
  sendDefaultPii: true,
  environment: import.meta.env.VITE_DEPLOYMENT_ENV,
  beforeSend(event) {
    // Title mutation errors by their mutation name (e.g. "uploadVideo: ...")
    // instead of the generic exception type ("ApiError: ..."). The mutation
    // tag is set in withSentryMutation; the issue title is derived from the
    // first exception's `type`, so we rewrite that. Grouping is unaffected —
    // it's driven by the custom fingerprint, not the type.
    const mutation = event.tags?.mutation;
    const exception = event.exception?.values?.[0];
    if (exception && typeof mutation === "string" && mutation !== "unknown") {
      exception.type = mutation;
    }
    return event;
  },
});

const defaultOptions: DefaultOptions = {
  mutations: {
    onError: withSentryMutation(),
  },
};

const queryClient = new QueryClient({ defaultOptions });

// The WebView's visibilitychange isn't a reliable signal for the app
// returning from background, so drive TanStack's focus state from the
// native app lifecycle — stale queries refetch on resume.
if (Capacitor.isNativePlatform()) {
  void import("@capacitor/app").then(({ App }) =>
    App.addListener("appStateChange", ({ isActive }) => {
      focusManager.setFocused(isActive);
    }),
  );
}

const rootElement = document.getElementById("root");
if (rootElement) {
  const tree = (
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster />
    </QueryClientProvider>
  );
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      {Capacitor.isNativePlatform() ? (
        tree
      ) : (
        <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
          {tree}
        </GoogleOAuthProvider>
      )}
    </React.StrictMode>,
  );
}

// For notifications (web only — native iOS/Android use FCM via Capacitor Firebase Messaging)
if (!Capacitor.isNativePlatform() && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch((err) =>
        console.error("Push notification registration failed:", err),
      );
  });
}
